import { BASE_URL, FALLBACK_DOMAIN } from '@constants/config';
import { ResponseObject } from './dataTypes';

type RequestData = Record<string, any> | null;
type AbortControllerType = AbortController;

// Seconds to cache rarely-changing navigation data (menus, events, promotions)
export const NAV_REVALIDATE = 60;

class ApiService {
    private baseURL: string;
    constructor(baseURL: string) {
        this.baseURL = baseURL + '/api';
      }

    private normalizeRequestData(data: Record<string, any> = {}): Record<string, string> {
        const normalized: Record<string, string> = {};

        Object.entries(data).forEach(([key, value]) => {
            if (value === undefined || value === null) {
                return;
            }

            if (key === 'domain') {
                const domainValue = typeof value === 'string' ? value.trim() : String(value).trim();
                normalized[key] = domainValue || FALLBACK_DOMAIN;
                return;
            }

            normalized[key] = String(value);
        });

        return normalized;
    }

    private async request<T>(route: string, options: RequestInit = {},
        controller: AbortControllerType = new AbortController(),
        revalidate?: number
    ): Promise<ResponseObject<T>> {

        const headers: HeadersInit = {
            'Content-Type': 'application/json',
            ...options.headers,
        };

        const config: RequestInit = revalidate
            ? {
                ...options,
                headers,
                signal: controller.signal,
                next: { revalidate },
            }
            : {
                ...options,
                headers,
                signal: controller.signal,
                cache: 'no-store',
                next: { revalidate: 0 }
            };

        try {
            const response = await fetch(this.baseURL + route, config);

            if (!response.ok) {
                return this.handleError<any>(`Server responded with status ${response.status} in api of ${route}`);
            }

            return await response.json();
        } catch (err) {
            return this.handleError<any>(err);
        }
    }

    async get<T>(route: string, data: Record<string, any> = {}, revalidate?: number): Promise<ResponseObject<T>> {
        const query = new URLSearchParams(data).toString();
        return this.request<T>(`${route}?${query}`, { method: 'GET' }, undefined, revalidate);
    }

    async post<T>(route: string, data: any, params: Record<string, any> = {}, revalidate?: number): Promise<ResponseObject<T>> {
        const query = new URLSearchParams(params).toString();
        const url = query ? `${route}?${query}` : route;
        // console.log('yrl',JSON.stringify(url), ` ${JSON.stringify(data)}`);
        return this.request<T>(url, { method: 'POST', body: JSON.stringify(data) }, undefined, revalidate);
    }

    async patch<T>(route: string, data: any, params: Record<string, any> = {}): Promise<ResponseObject<T>> {
        const query = new URLSearchParams(params).toString();
        const url = query ? `${route}?${query}` : route;
        return this.request<T>(url, { method: 'PATCH', body: JSON.stringify(data) });
    }

    async delete<T>(route: string, data: any, params: Record<string, any> = {}): Promise<ResponseObject<T>> {
        const query = new URLSearchParams(params).toString();
        const url = query ? `${route}?${query}` : route;
        return this.request<T>(url, { method: 'DELETE', body: JSON.stringify(data) });
    }

    private handleError<T>(err: any): ResponseObject<T | null> {
        return {
            status: false,
            message: typeof err === 'string' ? err : err?.message ?? 'An error occurred',
            data: null,
        };
    }
}

const apiService = new ApiService(BASE_URL);
export default apiService;
